/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" -- Legacy code: do not use as an example. */
import React, {
    ForwardRefRenderFunction,
    Ref,
    RefObject,
    forwardRef,
    useCallback,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import SmallArrowIcon from '@toucan/client/global/icons/legacy/small-arrow.svg';

import { useIsomorphicLayoutEffect } from '@toucan/client/global/nextjs';

import { useClickOutside } from '@toucan/client/shared/hooks/use-click-outside';
import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';
import { useWindowResizeCallback } from '@toucan/client/shared/hooks/use-window-resize';

import { useSelectA11y } from '@toucan/client/features/search/hooks/use-select-a11y';

import { styles } from './custom-select.styles';
import {
    CustomSelectAlignment,
    CustomSelectProps,
    StyledCustomSelectProps,
} from './types';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export const DEFAULT_TEST_ID = 'custom-select';

export const StyledContainer = styled.div`
    position: relative;
`;

// eslint-disable-next-line @mpb-com/mpb/single-styled-component -- Legacy code: do not use as an example.
export const StyledCustomSelect = styled.button<StyledCustomSelectProps>`
    ${styles}
`;

const CustomSelectWithRef: ForwardRefRenderFunction<
    HTMLElement,
    CustomSelectProps
> = (
    {
        'data-testid': dataTestId = DEFAULT_TEST_ID,
        closeMenuAriaLabel = '',
        openMenuAriaLabel = '',
        isActive,
        isOpen = false,
        id,
        label,
        children,
        onClickOut,
        onToggleOpen,
        ...otherProps
    }: CustomSelectProps,
    ref: Ref<HTMLElement>
) => {
    // Accessibility props for the custom select
    const { getRootProps, getToggleButtonProps, getLabelProps, getMenuProps } =
        useSelectA11y();

    const [alignment, setAlignment] = useState<CustomSelectAlignment>(
        CustomSelectAlignment.LEFT
    );

    // Handle closing the menu when clicking outside the panel
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(
        (ref as RefObject<HTMLButtonElement>)?.current || null
    );

    /**
     * Updates the positioning of the menu
     */
    const updateMenuPositioning = useCallback(() => {
        if (!isOpen) {
            setAlignment(CustomSelectAlignment.LEFT);
            return;
        }

        if (!menuRef.current) {
            return;
        }

        // Add class to apply default styles instantly
        menuRef.current.classList.add('open-left');

        const { left, width } = menuRef.current.getBoundingClientRect();
        if (left + width > window.outerWidth) {
            setAlignment(CustomSelectAlignment.RIGHT);
        } else {
            setAlignment(CustomSelectAlignment.LEFT);
        }

        menuRef.current.classList.remove('open-left');
    }, [isOpen]);

    useClickOutside([menuRef, buttonRef], () => {
        if (isOpen && onClickOut) {
            onClickOut();
        }
    });

    useKeyPress(
        buttonRef.current,
        ['ArrowDown', 'Down', 'ArrowUp', 'Up'],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
        (event: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            event.preventDefault();
            if (isOpen) {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
            onToggleOpen && onToggleOpen();
        }
    );

    useWindowResizeCallback(updateMenuPositioning);
    useIsomorphicLayoutEffect(updateMenuPositioning, [isOpen]);

    return (
        <StyledContainer
            data-testid={dataTestId}
            {...otherProps}
            {...getRootProps()}
        >
            <StyledCustomSelect
                isActive={isActive}
                isOpen={isOpen}
                alignment={alignment}
                onClick={onToggleOpen}
                {...getToggleButtonProps(
                    isOpen,
                    closeMenuAriaLabel,
                    openMenuAriaLabel
                )}
                {...getLabelProps(id)}
                ref={buttonRef}
            >
                {label}
                <SmallArrowIcon />
            </StyledCustomSelect>
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {isOpen && (
                <div
                    className="options"
                    {...getMenuProps(id)}
                    ref={menuRef}
                    data-testid={`${dataTestId}__options`}
                    data-alignment={alignment}
                >
                    {children}
                </div>
            )}
        </StyledContainer>
    );
};

export const CustomSelect = forwardRef(CustomSelectWithRef);
